// src/components/StockUniversePicker.jsx
//
// Universe selector for stock_equity scanner profiles & bot form.
//
// Two modes:
//   1) Predefined index (S&P 500 / NASDAQ-100 / Russell 1000) — fetched dynamically
//   2) Custom symbol list — paste-and-parse free-text input
//
// Output (via onChange): {
//   type: 'index' | 'custom',
//   index?: 'sp500'|'nasdaq100'|'russell1000',
//   symbols: ['AAPL', 'MSFT', ...]     // resolved list of tickers
// }
//
// The parent reads `value.symbols` for the actual filter.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, FormControl, Select, MenuItem, InputLabel,
  TextField, Chip, Alert, CircularProgress, Stack,
} from '@mui/material';
import { useUniverse, UNIVERSE_OPTIONS } from '../hooks/useUniverse';


export function StockUniversePicker({ value, onChange }) {
  const [mode, setMode] = useState(value?.type || 'index');
  const [indexName, setIndexName] = useState(value?.index || 'sp500');
  const [customText, setCustomText] = useState(
    value?.type === 'custom' ? (value.symbols || []).join(', ') : ''
  );

  // Fetch predefined universe when in index mode
  const { universe, loading, error } = useUniverse(mode === 'index' ? indexName : null);

  // Parse custom text → uppercase, deduped symbol list
  const customSymbols = useMemo(() => {
    if (!customText.trim()) return [];
    const seen = new Set();
    const out = [];
    for (const tok of customText.split(/[\s,;]+/)) {
      const cleaned = String(tok || '').trim().toUpperCase();
      // Tickers: 1-5 uppercase letters, optionally with a single dot (BRK.B)
      if (!/^[A-Z]{1,5}(\.[A-Z]{1,2})?$/.test(cleaned)) continue;
      if (seen.has(cleaned)) continue;
      seen.add(cleaned);
      out.push(cleaned);
    }
    return out;
  }, [customText]);

  // Resolve "symbols" based on mode
  const resolvedSymbols = useMemo(() => {
    if (mode === 'custom') return customSymbols;
    return Array.isArray(universe) ? universe.map(s => s.symbol) : [];
  }, [mode, customSymbols, universe]);

  // Bubble changes up
  useEffect(() => {
    onChange?.({
      type: mode,
      index: mode === 'index' ? indexName : null,
      symbols: resolvedSymbols,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, indexName, resolvedSymbols.length]);

  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#0f1218', borderColor: 'rgba(255,255,255,0.06)' }}>
      <Typography variant="caption" sx={{ color: '#9aa6bd', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
        Stock Universe
      </Typography>

      <Stack spacing={2}>
        {/* Mode + index selector */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Universe Source</InputLabel>
            <Select
              value={mode === 'custom' ? 'custom' : indexName}
              label="Universe Source"
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'custom') {
                  setMode('custom');
                } else {
                  setMode('index');
                  setIndexName(v);
                }
              }}
            >
              {UNIVERSE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{opt.label}</Typography>
                    <Typography variant="caption" sx={{ color: '#9aa6bd' }}>
                      {opt.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Loading / count chip */}
          {mode === 'index' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loading && <CircularProgress size={16} />}
              {!loading && resolvedSymbols.length > 0 && (
                <Chip
                  size="small"
                  label={`${resolvedSymbols.length} symbols`}
                  sx={{ bgcolor: '#052e16', color: '#46b88a', fontWeight: 600 }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Custom text input */}
        {mode === 'custom' && (
          <>
            <TextField
              label="Symbols (comma or space separated)"
              placeholder="AAPL, MSFT, NVDA, TSLA, GOOGL"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              multiline
              rows={3}
              fullWidth
              size="small"
              helperText={`${customSymbols.length} valid symbol${customSymbols.length === 1 ? '' : 's'} parsed`}
            />
            {customSymbols.length > 0 && customSymbols.length <= 50 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {customSymbols.slice(0, 50).map(sym => (
                  <Chip key={sym} size="small" label={sym} sx={{ fontFamily: 'monospace' }} />
                ))}
              </Box>
            )}
          </>
        )}

        {/* Errors */}
        {error && (
          <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
            Could not load {UNIVERSE_OPTIONS.find(o => o.value === indexName)?.label}: {error}.
            Custom list still works.
          </Alert>
        )}

        {/* Helper text */}
        <Typography variant="caption" sx={{ color: '#666' }}>
          The scanner will only evaluate symbols in this universe.
          {mode === 'index' && ' Index constituents update daily from public sources.'}
          {mode === 'custom' && ' Enter US-listed equity tickers (e.g. AAPL, BRK.B).'}
        </Typography>
      </Stack>
    </Paper>
  );
}
